/**
 * Environment-aware logging utility
 * Only logs in development environment, silent in production
 */

interface Logger {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: (message: string, ...args: any[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  warn: (message: string, ...args: any[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  info: (message: string, ...args: any[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  debug: (message: string, ...args: any[]) => void;
}

class DevLogger implements Logger {
  private isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error(message: string, ...args: any[]): void {
    if (!this.isDevelopment) return;
    console.error(`[ERROR] ${message}`, ...args);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  warn(message: string, ...args: any[]): void {
    if (!this.isDevelopment) return;
    console.warn(`[WARN] ${message}`, ...args);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  info(message: string, ...args: any[]): void {
    if (!this.isDevelopment) return;
    console.info(`[INFO] ${message}`, ...args);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  debug(message: string, ...args: any[]): void {
    if (!this.isDevelopment) return;
    console.debug(`[DEBUG] ${message}`, ...args);
  }
}

export const logger = new DevLogger();

/**
 * Development-only console logging utility
 * Always logs to console in development, silent in production
 */
export const devLog = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  info: (...args: any[]): void => {
    if (import.meta.env.DEV) console.log('[DEV]', ...args);
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: (...args: any[]): void => {
    if (import.meta.env.DEV) console.error('[DEV]', ...args);
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  warn: (...args: any[]): void => {
    if (import.meta.env.DEV) console.warn('[DEV]', ...args);
  },
};

/**
 * Custom error classes for specific error types
 */
export class ValidationError extends Error {
  public field?: string;
  public missingColumns?: string[];
  
  constructor(message: string, field?: string, missingColumns?: string[]) {
    super(message);
    this.name = 'ValidationError';
    this.field = field;
    this.missingColumns = missingColumns;
  }
}

export class NetworkError extends Error {
  public status?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public response?: any;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(message: string, status?: number, response?: any) {
    super(message);
    this.name = 'NetworkError';
    this.status = status;
    this.response = response;
  }
}

export class FileSizeError extends Error {
  public actualSize?: number;
  public maxSize?: number;
  
  constructor(message: string, actualSize?: number, maxSize?: number) {
    super(message);
    this.name = 'FileSizeError';
    this.actualSize = actualSize;
    this.maxSize = maxSize;
  }
}

export class ProcessingError extends Error {
  public jobId?: string;
  public step?: string;
  
  constructor(message: string, jobId?: string, step?: string) {
    super(message);
    this.name = 'ProcessingError';
    this.jobId = jobId;
    this.step = step;
  }
}

export class PaymentError extends Error {
  public paymentId?: string;
  public code?: string;

  constructor(message: string, paymentId?: string, code?: string) {
    super(message);
    this.name = 'PaymentError';
    this.paymentId = paymentId;
    this.code = code;
  }
}

export class NoHitError extends Error {
  public requestId?: string;
  public aiOverview?: {
    overview: string;
    riskAssessment: string;
    recommendedStrategy: string;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(message: string, requestId?: string, aiOverview?: any) {
    super(message);
    this.name = 'NoHitError';
    this.requestId = requestId;
    this.aiOverview = aiOverview;
  }
}

export class PartialSuccessError extends Error {
  public requestId?: string;
  public successfulOperation?: string; // e.g., 'scoring'
  public failedOperation?: string; // e.g., 'enrichment'
  public searchResult?: any; // The partial result we did get

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(message: string, requestId?: string, successfulOperation?: string, failedOperation?: string, searchResult?: any) {
    super(message);
    this.name = 'PartialSuccessError';
    this.requestId = requestId;
    this.successfulOperation = successfulOperation;
    this.failedOperation = failedOperation;
    this.searchResult = searchResult;
  }
}